import { Grid } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  HdFormikDropDown,
} from '../../../../form/formik/components';
import { GoogleAuth } from '../../../../connector/connector-form/components/google-auth/GoogleAuthFormik';
import { LoadedAt, SanitizeName } from '../../../../connector/connector-form/components/connector-reusable-options/ConnectorReusableOptions';
import HdFormControl from '../../../../form/components/form-control/HdFormControl';
import AdvancedConfig from '../../../../connector/connector-form/components/advanced-config-wrapper/AdvancedConfigWrapper';
import { HdLink } from '../../../../components';
import useDontMountAtFirst from '../../../../core/hooks/useDontMountAtFirst';
import { getDataIdGenerator } from '../../../../core/utils/generateDataId';
import { AUTH_FORM_FIELD_NAME, BigqueryResourceType, HEVO_GCS_BUCKET_OPTION } from './model';
import { PromiseCancelError } from '../../../../core/utils/promise';
import { StreamingWritesConfig } from './StreamingWritesConfig';
import styles from './BigQuery.module.scss';
import useTeamSettingsService from '../../../team/hooks/useTeamSettingsService';
import useAnalyticsTracker from '../../../../analytics/hooks/useAnalyticsTracker';
import { ConfigDestinationTypeBaseProps } from '../interface';
import { REFRESH_ACTION_MAP } from './constants';
import { fetchResourceListFn, groupResourceListOnLocationInReactFormat } from './utils';

type ResourceItem = { id: string; name?: string; apiLocation?: string | null };

interface BigQueryFormValues {
  auth: { id: number; email: string };
  projectId: { id: string } | null;
  dataset: { id: string; apiLocation: string } | null;
  bucket: { id: string; apiLocation: string; internalBucket?: boolean } | null;
  streamingWrites: boolean;
}

const fetchResource = (
  state: BigQueryFormValues,
  resource: BigqueryResourceType,
  setResourceList: (list: ResourceItem[]) => void,
  setLoading: (loading: boolean) => void,
  abortControllerRef: MutableRefObject<AbortController>,
  onResolved?: (resourceList: ResourceItem[]) => void,
  // When provided, called on API error instead of onResolved([]). Lets the caller
  // distinguish "fetch failed — keep current selection" from "fetch succeeded, resource
  // not found — clear selection". Used by the switch-back restore path so a transient
  // API failure does not wipe the just-restored bucket/dataset.
  onError?: () => void
) => {
  setLoading(true);
  setResourceList([]);

  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
    // eslint-disable-next-line no-param-reassign
    abortControllerRef.current = new AbortController();
  }

  // Capture the signal for this specific fetch call. When a newer fetch starts for the
  // same resource (via the same abortControllerRef) it aborts this signal, which lets us
  // detect stale callbacks and silently drop them — preventing an in-flight service-account
  // fetch from clobbering restored values after the user switches back to their original
  // account (race condition: switch-back fires case (b) restore, then old fetch resolves
  // and calls clearAllSelections or sets bucket=null).
  const { signal } = abortControllerRef.current;

  fetchResourceListFn(state, resource, {
    signal
  })
    .then((resourceList: ResourceItem[]) => {
      // A newer fetch was started for this resource; discard the stale result.
      if (signal.aborted) {
        return;
      }

      if (resource === BigqueryResourceType.PROJECT) {
        setResourceList(resourceList);
      } else if (resource === BigqueryResourceType.DATASET) {
        setResourceList(groupResourceListOnLocationInReactFormat(resourceList));
      } else if (resource === BigqueryResourceType.BUCKET) {
        const list = resourceList.length > 0 ? resourceList : [HEVO_GCS_BUCKET_OPTION];
        setResourceList(groupResourceListOnLocationInReactFormat(list));
      }

      setLoading(false);
      onResolved?.(resourceList || []);
    })
    .catch((err: unknown) => {
      if (err instanceof PromiseCancelError) {
        return;
      }

      // Discard the error callback if a newer fetch already superseded this one.
      if (signal.aborted) {
        return;
      }

      setLoading(false);

      if (resource === BigqueryResourceType.BUCKET) {
        setResourceList([HEVO_GCS_BUCKET_OPTION]);
      }

      if (onError) {
        onError();
      } else {
        // The new account could not list this resource; treat it as unavailable so any
        // carried-over selection that depends on it is cleared by the caller.
        onResolved?.([]);
      }
    });
};


function RefreshDropdownDataAdornment({
  values,
  name,
  setResourceList,
  setLoading,
  abortControllerRef
}: {
  values: BigQueryFormValues;
  name: BigqueryResourceType;
  setResourceList: (list: ResourceItem[]) => void;
  setLoading: (loading: boolean) => void;
  abortControllerRef: MutableRefObject<AbortController>;
}) {
  const { eventTrack } = useAnalyticsTracker();

  const handleMouseClick = useCallback(() => {
    eventTrack({
      action: REFRESH_ACTION_MAP[name],
      properties: {
        projectId: values?.projectId?.id
      }
    });

    fetchResource(values, name, setResourceList, setLoading, abortControllerRef);
  }, [values, name, setResourceList, setLoading, abortControllerRef, eventTrack]);

  return (
    <HdLink
      tag='a'
      className={`${styles.refreshAdornment} text-primary `}
      onMouseDown={handleMouseClick}
      direction='right'
      icon='refresh'
      dataId={`bigquery-edit-${name}-refresh`}
    >
      Refresh
    </HdLink>
  );
}

export default function BigQueryEdit({
  isEditing,
  formMetaData,
  hevoEntityFor,
  isDestinationModeWarehouse
}: ConfigDestinationTypeBaseProps) {
  const { values, setValues, setFieldValue, setFieldTouched } = useFormikContext<BigQueryFormValues>();

  const { projects = [], buckets = [], datasets = [] } = formMetaData;

  const [projectList, setProjectList] = useState<ResourceItem[]>(projects);
  const [datasetList, setDatasetList] = useState<ResourceItem[]>(datasets);
  const [bucketsList, setBucketsList] = useState<ResourceItem[]>(buckets);
  const [projectLoading, setProjectLoading] = useState(false);
  const [datasetLoading, setDatasetLoading] = useState(false);
  const [bucketLoading, setBucketLoading] = useState(false);
  const [bucketLocationFilter, setBucketLocationFilter] = useState<string>();

  const projectAbortControllerRef = useRef(new AbortController());
  const datasetAbortControllerRef = useRef(new AbortController());
  const bucketAbortControllerRef = useRef(new AbortController());

  // In edit mode the project/dataset/bucket are prefilled from the existing config. Switching the
  // account (e.g. user account -> service account) must carry them over as-is instead of resetting
  // them, so the user is not forced to re-pick and the backend does not flag a spurious
  // "Bucket/Project ID/Dataset changed" warning.
  //
  // The account field hydrates asynchronously on initial edit load (auth goes from
  // undefined -> the originally stored account id), and that transition must NOT be treated as a
  // user-initiated switch. We therefore remember the originally loaded account id and only preserve
  // the resources on the FIRST transition AWAY from it. Any later switch is deliberate and resets,
  // EXCEPT switching back to the original account which restores the original selections.
  const originalAuthIdRef = useRef<number | null>(isEditing ? values.auth?.id ?? null : null);
  const preserveResourcesOnAuthChangeRef = useRef(isEditing);

  // Stores the originally loaded project/dataset/bucket so they can be restored when the user
  // switches back to the original account. Populated on the first switch away (preserve path),
  // at which point the form values are guaranteed to be fully hydrated.
  const originalResourceValuesRef = useRef<Pick<BigQueryFormValues, 'projectId' | 'dataset' | 'bucket'> | null>(null);

  // When we restore original values on switch-back, the projectId change would normally
  // trigger the projectId effect which clears dataset/bucket. This flag suppresses that.
  const skipProjectChangeFetchOnceRef = useRef(false);

  // Capture the originally loaded account id once it hydrates, without treating the hydration as a
  // switch (the account-change effect below ignores changes while this is still being established).
  useEffect(() => {
    if (isEditing && originalAuthIdRef.current == null && values.auth?.id != null) {
      originalAuthIdRef.current = values.auth.id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.auth?.id]);

  const { getTeamSettings } = useTeamSettingsService();

  const isStreamModeAllowed = getTeamSettings()?.wh_streaming_writes === true;
  const useStreamMode = isStreamModeAllowed && values.streamingWrites;

  const clearAllSelections = useCallback(() => {
    setValues(cv => ({ ...cv, projectId: null, dataset: null, bucket: null }));
    setFieldTouched('projectId', false, false);
    setFieldTouched('dataset', false, false);
    setFieldTouched('bucket', false, false);
  }, [setValues, setFieldTouched]);

  useDontMountAtFirst(() => {
    const currentAuthId = values.auth?.id ?? null;

    // Switching the account TYPE briefly clears the account (auth -> null) before the new account
    // settles (auth -> new id). Ignore that intermediate empty state; we only act once a real
    // account id is present.
    if (currentAuthId == null) {
      return;
    }

    // When the current account matches the originally loaded account it is either:
    //   a) async hydration on initial edit load (not a user action) — skip, or
    //   b) user switching back to the original account — restore original selections.
    if (isEditing && currentAuthId === originalAuthIdRef.current) {
      if (preserveResourcesOnAuthChangeRef.current) {
        // Case (a): first appearance of the auth id on load, not a deliberate switch.
        return;
      }

      // Case (b): deliberate switch back. Restore the original project/dataset/bucket,
      // refetch the dropdown lists for the original account, then drop any selection
      // the original account can no longer access.
      const original = originalResourceValuesRef.current;
      if (!original) {
        return;
      }

      const restoredProject = original.projectId;
      const restoredProjectId = restoredProject?.id;
      const restoredDatasetId = original.dataset?.id;
      const restoredBucketId = original.bucket?.id;
      const restoredBucketIsInternal = original.bucket?.internalBucket;

      // Restore all three fields optimistically. Set the skip flag first so the
      // projectId-change effect (which fires on the next render because projectId
      // transitions from null → restored id) does not clobber dataset/bucket.
      skipProjectChangeFetchOnceRef.current = true;
      setValues(currentValues => ({
        ...currentValues,
        projectId: restoredProject,
        dataset: original.dataset,
        bucket: original.bucket
      }));
      setFieldTouched('projectId', false, false);
      setFieldTouched('dataset', false, false);
      setFieldTouched('bucket', false, false);

      const stateForFetch = { ...values, projectId: restoredProject };

      fetchResource(
        stateForFetch,
        BigqueryResourceType.PROJECT,
        setProjectList,
        setProjectLoading,
        projectAbortControllerRef,
        projectList => {
          const projectStillValid =
            !!restoredProjectId && projectList.some(p => p?.id === restoredProjectId);
          if (!projectStillValid) {
            clearAllSelections();
          }
        }
      );

      if (restoredProjectId) {
        fetchResource(
          stateForFetch,
          BigqueryResourceType.DATASET,
          setDatasetList,
          setDatasetLoading,
          datasetAbortControllerRef,
          datasetList => {
            const datasetStillValid =
              !!restoredDatasetId && datasetList.some(d => d?.id === restoredDatasetId);
            if (!datasetStillValid) {
              setFieldValue('dataset', null);
              setFieldTouched('dataset', false, false);
            }
          },
          // On API error: keep the restored dataset — a transient failure should not wipe
          // the value the user just switched back to get.
          () => {}
        );

        fetchResource(
          stateForFetch,
          BigqueryResourceType.BUCKET,
          setBucketsList,
          setBucketLoading,
          bucketAbortControllerRef,
          bucketList => {
            // The Hevo-managed (internal) bucket is always available; only validate real buckets.
            const bucketStillValid =
              restoredBucketIsInternal ||
              (!!restoredBucketId && bucketList.some(b => b?.id === restoredBucketId));
            if (!bucketStillValid) {
              setFieldValue('bucket', null);
              setFieldTouched('bucket', false, false);
            }
          },
          // On API error: keep the restored bucket — a transient failure (expired token,
          // flaky network, permission hiccup) must not wipe the prefilled GCS bucket.
          () => {}
        );
      }

      return;
    }

    // First switch AWAY from the originally loaded account in edit mode: try to carry the prefilled
    // project/dataset/bucket over to the new account and refresh the dropdown lists for it.
    // Switching the account only changes the credential Hevo authenticates with, not where data is
    // written, so when the new account CAN access the same resources they stay selected (no re-pick,
    // no spurious "Bucket/Project ID/Dataset changed" warning). But if the new account CANNOT access
    // a resource, the carried-over value is stale and must be cleared - we validate each selection
    // against the freshly fetched list and drop anything the new account does not return.
    if (preserveResourcesOnAuthChangeRef.current) {
      preserveResourcesOnAuthChangeRef.current = false;

      const preservedProject = values.projectId;
      const preservedProjectId = preservedProject?.id;

      // Capture original values here — at this point the form is guaranteed fully
      // hydrated, so these are the true saved config values.
      originalResourceValuesRef.current = {
        projectId: values.projectId ?? null,
        dataset: values.dataset ?? null,
        bucket: values.bucket ?? null
      };
      const preservedDatasetId = values.dataset?.id;
      const preservedBucketId = values.bucket?.id;
      const preservedBucketIsInternal = values.bucket?.internalBucket;

      // Build an explicit state for the resource fetches so they always carry the new account AND
      // the preserved project, independent of how React has batched the in-flight form updates
      // (the account-type switch transiently clears other fields, which would otherwise make the
      // dataset/bucket lookups query projects/undefined). fetchResourceListFn reads `auth` and
      // `projectId` off this state, so pin both explicitly.
      const stateForFetch = {
        ...values,
        auth: values.auth,
        projectId: preservedProject
      };

      fetchResource(
        stateForFetch,
        BigqueryResourceType.PROJECT,
        setProjectList,
        setProjectLoading,
        projectAbortControllerRef,
        projectList => {
          const projectStillValid =
            !!preservedProjectId && projectList.some(p => p?.id === preservedProjectId);
          if (!projectStillValid) {
            // The new account cannot see the project, so the dataset/bucket under it are invalid
            // too. Clear everything and let the user pick afresh from the new account's projects.
            clearAllSelections();
          }
        }
      );

      if (preservedProjectId) {
        fetchResource(
          stateForFetch,
          BigqueryResourceType.DATASET,
          setDatasetList,
          setDatasetLoading,
          datasetAbortControllerRef,
          datasetList => {
            const datasetStillValid =
              !!preservedDatasetId && datasetList.some(d => d?.id === preservedDatasetId);
            if (!datasetStillValid) {
              setFieldValue('dataset', null);
              setFieldTouched('dataset', false, false);
            }
          }
        );

        fetchResource(
          stateForFetch,
          BigqueryResourceType.BUCKET,
          setBucketsList,
          setBucketLoading,
          bucketAbortControllerRef,
          bucketList => {
            // The Hevo-managed (internal) bucket is always available; only validate real buckets.
            const bucketStillValid =
              preservedBucketIsInternal ||
              (!!preservedBucketId && bucketList.some(b => b?.id === preservedBucketId));
            if (!bucketStillValid) {
              setFieldValue('bucket', null);
              setFieldTouched('bucket', false, false);
            }
          }
        );
      }

      return;
    }

    setFieldValue('projectId', null);
    setFieldTouched('projectId', false, false);

    fetchResource(
      values,
      BigqueryResourceType.PROJECT,
      setProjectList,
      setProjectLoading,
      projectAbortControllerRef
    );
  }, [values.auth?.id]);

  useEffect(() => {
    if (!values.dataset?.id) {
      return;
    }

    if (
      values.dataset?.apiLocation?.toUpperCase() !==
        values.bucket?.apiLocation?.toUpperCase() &&
      !values.bucket?.internalBucket
    ) {
      setFieldValue('bucket', null);
      setFieldTouched('bucket', false, false);
    }

    setBucketLocationFilter(values.dataset.apiLocation?.toUpperCase());
  }, [values.dataset?.id]);

  const filteredBucketList = useMemo(() => {
    const buckets = bucketsList || [];
    const inRegion = (b: ResourceItem) =>
      !bucketLocationFilter || b.apiLocation?.toUpperCase() === bucketLocationFilter;
    const list = [...buckets.filter(inRegion), ...buckets.filter(b => !inRegion(b))];
    return list.length > 0 ? list : [HEVO_GCS_BUCKET_OPTION];
  }, [bucketLocationFilter, bucketsList]);

  useDontMountAtFirst(() => {
    // Skip when the auth switch-back path already restored dataset/bucket directly.
    if (skipProjectChangeFetchOnceRef.current) {
      skipProjectChangeFetchOnceRef.current = false;
      return;
    }

    setValues(currentValues => ({
      ...currentValues,
      bucket: null,
      dataset: null
    }));
    setFieldTouched('dataset', false, false);
    setFieldTouched('bucket', false, false);

    if (values.projectId?.id) {
      fetchResource(
        values,
        BigqueryResourceType.DATASET,
        setDatasetList,
        setDatasetLoading,
        datasetAbortControllerRef
      );

      fetchResource(
        values,
        BigqueryResourceType.BUCKET,
        setBucketsList,
        setBucketLoading,
        bucketAbortControllerRef
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.projectId?.id]);

  const dataIdGenerator = useCallback(getDataIdGenerator('bigquery-edit'), []);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item sm={12} md={12}>
          <HdFormControl>
            <Field
              name={AUTH_FORM_FIELD_NAME}
              provider='BIGQUERY'
              displayName='Big Query'
              hevoEntity={hevoEntityFor}
              component={GoogleAuth}
            />
          </HdFormControl>
        </Grid>

        <Grid item md={isEditing ? 6 : 12} sm={isEditing ? 6 : 12}>
          <HdFormControl>
            <Field
              name='projectId'
              label='Project'
              showLoading={projectLoading}
              TopAdornment={RefreshDropdownDataAdornment}
              topAdornmentProps={{
                values,
                name: BigqueryResourceType.PROJECT,
                setLoading: setProjectLoading,
                setResourceList: setProjectList,
                abortControllerRef: projectAbortControllerRef
              }}
              component={HdFormikDropDown}
              required
              options={projectList}
            />
          </HdFormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item md={6} sm={12}>
          <HdFormControl>
            <Field
              label='Dataset'
              name='dataset'
              group
              showLoading={datasetLoading}
              TopAdornment={RefreshDropdownDataAdornment}
              topAdornmentProps={{
                values,
                abortControllerRef: datasetAbortControllerRef,
                name: BigqueryResourceType.DATASET,
                setLoading: setDatasetLoading,
                setResourceList: setDatasetList
              }}
              options={datasetList}
              component={HdFormikDropDown}
              required
            />
          </HdFormControl>
        </Grid>

        {!useStreamMode && (
          <Grid item md={6} sm={12}>
            <HdFormControl>
              <Field
                options={filteredBucketList}
                required
                group
                showLoading={bucketLoading}
                component={HdFormikDropDown}
                TopAdornment={RefreshDropdownDataAdornment}
                topAdornmentProps={{
                  values,
                  abortControllerRef: bucketAbortControllerRef,
                  name: BigqueryResourceType.BUCKET,
                  setLoading: setBucketLoading,
                  setResourceList: setBucketsList
                }}
                label='GCS bucket'
                name='bucket'
              />
            </HdFormControl>
          </Grid>
        )}
      </Grid>

      {!isDestinationModeWarehouse ? (
        <AdvancedConfig showBorderBottom>
          <div className='w-100'>
            <LoadedAt />

            <SanitizeName
              disabled={!!isEditing}
              dataId={dataIdGenerator('sanitize-name')}
            />

            {isStreamModeAllowed ? (
              <StreamingWritesConfig dataId={dataIdGenerator('')} />
            ) : null}
          </div>
        </AdvancedConfig>
      ) : null}
    </>
  );
}
